import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, CalendarRange, Inbox, Loader2, LogOut, MessageSquare, RefreshCw, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wordmark } from "@/components/Navbar";
import { API, TRAILERS, formatApiError } from "@/lib/site";

const TOKEN_KEY = "looxury_admin_token";

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "quotes", label: "Quote Requests", icon: Inbox },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "availability", label: "Availability", icon: CalendarRange },
];

const trailerName = (id) => (id === "all" ? "All trailers" : TRAILERS.find((t) => t.id === id)?.name || id);
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [data, setData] = useState({ bookings: [], quotes: [], messages: [] });
  const [blocks, setBlocks] = useState([]);
  const emptyBlock = { trailer: "all", start_date: "", end_date: "", note: "" };
  const [blockForm, setBlockForm] = useState(emptyBlock);
  const [fetching, setFetching] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setToken(null);
  };

  const load = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [b, q, m, a] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }),
        axios.get(`${API}/availability`),
      ]);
      setData({ bookings: b.data, quotes: q.data, messages: m.data });
      setBlocks(a.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        logout();
      } else {
        toast.error("Couldn't load submissions.");
      }
    } finally {
      setFetching(false);
    }
  }, [token]);

  const setStatus = async (kind, id, status) => {
    try {
      await axios.patch(`${API}/admin/${kind}/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setData((d) => ({ ...d, [kind]: d[kind].map((r) => (r.id === id ? { ...r, status } : r)) }));
    } catch {
      toast.error("Couldn't update status.");
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockForm.start_date || !blockForm.end_date) return toast.error("Pick both dates.");
    try {
      const { data: saved } = await axios.post(`${API}/admin/availability`, blockForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlocks((prev) => [...prev, saved].sort((x, y) => x.start_date.localeCompare(y.start_date)));
      setBlockForm(emptyBlock);
      toast.success("Dates marked as booked.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeBlock = async (id) => {
    try {
      await axios.delete(`${API}/admin/availability/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch {
      toast.error("Couldn't remove that block.");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-950 px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-stone-800 bg-stone-900 p-10" data-testid="admin-login-form">
          <Wordmark dark />
          <p className="mt-2 text-xs font-semibold tracking-[0.25em] uppercase text-brand-bronze">Owner sign in</p>
          <Input
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-8 rounded-none border-stone-700 bg-stone-950 py-6 text-stone-100 placeholder:text-stone-500 focus-visible:ring-brand-bronze"
            data-testid="admin-email-input"
            aria-label="Admin email"
          />
          <Input
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-4 rounded-none border-stone-700 bg-stone-950 py-6 text-stone-100 placeholder:text-stone-500 focus-visible:ring-brand-bronze"
            data-testid="admin-password-input"
            aria-label="Admin password"
          />
          {error && (
            <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-6 flex w-full items-center justify-center gap-2 bg-brand-bronze px-6 py-4 text-sm tracking-wide text-stone-900 transition-colors duration-300 hover:bg-brand-bronze-light disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Sign In
          </button>
          <Link to="/" data-testid="admin-back-link" className="mt-6 block text-center text-xs tracking-wide text-stone-500 hover:text-stone-300">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-dashboard">
      <header className="border-b border-stone-200 bg-stone-950">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Wordmark dark />
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-stone-700 px-4 py-2 text-xs tracking-wide text-stone-300 transition-colors duration-300 hover:border-brand-bronze hover:text-brand-bronze"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} aria-hidden="true" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-stone-700 px-4 py-2 text-xs tracking-wide text-stone-300 transition-colors duration-300 hover:border-brand-bronze hover:text-brand-bronze"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
        <h1 className="font-serif text-3xl font-light text-stone-900">Submissions</h1>
        <p className="mt-2 text-sm font-light text-stone-500">Booking requests, quote requests, and contact messages from the website.</p>

        <div className="mt-8 flex gap-2" role="tablist" aria-label="Submission types">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide transition-colors duration-300 ${
                tab === t.id ? "bg-stone-900 text-stone-50" : "border border-stone-300 text-stone-600 hover:border-stone-500"
              }`}
            >
              <t.icon className="h-4 w-4" aria-hidden="true" />
              {t.label}
              <span className="ml-1 bg-brand-bronze px-2 py-0.5 text-xs text-stone-900" data-testid={`admin-count-${t.id}`}>
                {(t.id === "availability" ? blocks : data[t.id]).length}
              </span>
            </button>
          ))}
        </div>

        {tab === "availability" ? (
          <AvailabilityPanel blocks={blocks} form={blockForm} setForm={setBlockForm} onAdd={addBlock} onRemove={removeBlock} />
        ) : (
          <div className="mt-6 overflow-x-auto border border-stone-200 bg-white" data-testid="admin-table-wrapper">
            {tab === "bookings" && <BookingsTable rows={data.bookings} onSet={setStatus} />}
            {tab === "quotes" && <QuotesTable rows={data.quotes} onSet={setStatus} />}
            {tab === "messages" && <MessagesTable rows={data.messages} onSet={setStatus} />}
          </div>
        )}
      </main>
    </div>
  );
}

const headCls = "text-xs font-semibold tracking-[0.15em] uppercase text-stone-500";
const cellCls = "text-sm font-light text-stone-700";

const Empty = ({ label, colSpan = 8 }) => (
  <TableRow>
    <TableCell colSpan={colSpan} className="py-12 text-center text-sm font-light text-stone-400" data-testid="admin-empty-state">
      No {label} yet — new website submissions will appear here.
    </TableCell>
  </TableRow>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const STATUS_STYLES = {
  new: "border-stone-300 text-stone-500",
  contacted: "border-brand-bronze/50 bg-brand-bronze-light/40 text-brand-bronze-dark",
  confirmed: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

function StatusCell({ row, kind, onSet }) {
  const status = row.status || "new";
  return (
    <div className="flex flex-col gap-2">
      <span
        className={`inline-block w-fit border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${STATUS_STYLES[status]}`}
        data-testid={`status-badge-${kind}-${row.id.slice(0, 8)}`}
      >
        {status}
      </span>
      <div className="flex gap-1.5">
        {status !== "contacted" && status !== "confirmed" && (
          <button
            onClick={() => onSet(kind, row.id, "contacted")}
            data-testid={`mark-contacted-${kind}-${row.id.slice(0, 8)}`}
            className="border border-stone-300 px-2 py-1 text-[11px] tracking-wide text-stone-600 transition-colors duration-300 hover:border-brand-bronze hover:text-brand-bronze-dark"
          >
            Contacted
          </button>
        )}
        {status !== "confirmed" && (
          <button
            onClick={() => onSet(kind, row.id, "confirmed")}
            data-testid={`mark-confirmed-${kind}-${row.id.slice(0, 8)}`}
            className="border border-stone-300 px-2 py-1 text-[11px] tracking-wide text-stone-600 transition-colors duration-300 hover:border-emerald-400 hover:text-emerald-700"
          >
            Confirmed
          </button>
        )}
      </div>
    </div>
  );
}

function BookingsTable({ rows, onSet }) {
  return (
    <Table data-testid="admin-bookings-table">
      <TableHeader>
        <TableRow>
          {["Received", "Trailer", "Event", "Venue", "Dates", "Guests", "Contact", "Status", "Notes"].map((h) => (
            <TableHead key={h} className={headCls}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && <Empty label="booking requests" colSpan={9} />}
        {rows.map((r) => (
          <TableRow key={r.id} data-testid={`admin-booking-row-${r.id.slice(0, 8)}`}>
            <TableCell className={cellCls}>{fmt(r.created_at)}</TableCell>
            <TableCell className="text-sm text-stone-900">{trailerName(r.trailer)}</TableCell>
            <TableCell className={cellCls}>{r.event_type}</TableCell>
            <TableCell className={cellCls}>{r.venue_address}, {r.city}</TableCell>
            <TableCell className={cellCls}>{r.start_date} → {r.end_date}</TableCell>
            <TableCell className={cellCls}>{r.guests || "—"}</TableCell>
            <TableCell className={cellCls}>
              {r.name}
              <br />
              <a href={`mailto:${r.email}`} className="text-brand-bronze-dark">{r.email}</a>
              <br />
              <a href={`tel:${r.phone}`} className="text-brand-bronze-dark">{r.phone}</a>
            </TableCell>
            <TableCell><StatusCell row={r} kind="bookings" onSet={onSet} /></TableCell>
            <TableCell className={`${cellCls} max-w-56`}>{r.notes || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function QuotesTable({ rows, onSet }) {
  return (
    <Table data-testid="admin-quotes-table">
      <TableHeader>
        <TableRow>
          {["Received", "Name", "Contact", "Event type", "Event date", "Location", "Status", "Message"].map((h) => (
            <TableHead key={h} className={headCls}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && <Empty label="quote requests" colSpan={8} />}
        {rows.map((r) => (
          <TableRow key={r.id} data-testid={`admin-quote-row-${r.id.slice(0, 8)}`}>
            <TableCell className={cellCls}>{fmt(r.created_at)}</TableCell>
            <TableCell className="text-sm text-stone-900">{r.name}</TableCell>
            <TableCell className={cellCls}>
              <a href={`mailto:${r.email}`} className="text-brand-bronze-dark">{r.email}</a>
              {r.phone && (
                <>
                  <br />
                  <a href={`tel:${r.phone}`} className="text-brand-bronze-dark">{r.phone}</a>
                </>
              )}
            </TableCell>
            <TableCell className={cellCls}>{r.event_type || "—"}</TableCell>
            <TableCell className={cellCls}>{r.event_date || "—"}</TableCell>
            <TableCell className={cellCls}>{r.location || "—"}</TableCell>
            <TableCell><StatusCell row={r} kind="quotes" onSet={onSet} /></TableCell>
            <TableCell className={`${cellCls} max-w-64`}>{r.message || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MessagesTable({ rows, onSet }) {
  return (
    <Table data-testid="admin-messages-table">
      <TableHeader>
        <TableRow>
          {["Received", "Name", "Contact", "Status", "Message"].map((h) => (
            <TableHead key={h} className={headCls}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && <Empty label="messages" colSpan={5} />}
        {rows.map((r) => (
          <TableRow key={r.id} data-testid={`admin-message-row-${r.id.slice(0, 8)}`}>
            <TableCell className={cellCls}>{fmt(r.created_at)}</TableCell>
            <TableCell className="text-sm text-stone-900">{r.name}</TableCell>
            <TableCell className={cellCls}>
              <a href={`mailto:${r.email}`} className="text-brand-bronze-dark">{r.email}</a>
              {r.phone && (
                <>
                  <br />
                  <a href={`tel:${r.phone}`} className="text-brand-bronze-dark">{r.phone}</a>
                </>
              )}
            </TableCell>
            <TableCell><StatusCell row={r} kind="messages" onSet={onSet} /></TableCell>
            <TableCell className={`${cellCls} max-w-96`}>{r.message}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AvailabilityPanel({ blocks, form, setForm, onAdd, onRemove }) {
  return (
    <div className="mt-6 space-y-6" data-testid="availability-panel">
      <form onSubmit={onAdd} className="flex flex-wrap items-end gap-4 border border-stone-200 bg-white p-6" data-testid="availability-form">
        <div>
          <label className="mb-2 block text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Trailer</label>
          <Select value={form.trailer} onValueChange={(v) => setForm({ ...form, trailer: v })}>
            <SelectTrigger className="w-48 rounded-none border-stone-300 bg-white" data-testid="availability-trailer-select" aria-label="Trailer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="availability-trailer-all">All trailers</SelectItem>
              {TRAILERS.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`availability-trailer-${t.id}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Booked from</label>
          <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-44 rounded-none border-stone-300" data-testid="availability-start-input" />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Until</label>
          <Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-44 rounded-none border-stone-300" data-testid="availability-end-input" />
        </div>
        <div className="min-w-40 flex-1">
          <label className="mb-2 block text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Note (optional)</label>
          <Input placeholder="e.g. Harrison wedding" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-none border-stone-300" data-testid="availability-note-input" />
        </div>
        <button type="submit" data-testid="availability-add-button" className="bg-stone-900 px-6 py-2.5 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900">
          Block Dates
        </button>
      </form>

      <div className="overflow-x-auto border border-stone-200 bg-white">
        <Table data-testid="availability-table">
          <TableHeader>
            <TableRow>
              {["Trailer", "Booked dates", "Note", ""].map((h) => (
                <TableHead key={h} className={headCls}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {blocks.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center text-sm font-light text-stone-400" data-testid="availability-empty-state">
                  No dates blocked yet — every date shows as available on the booking page.
                </TableCell>
              </TableRow>
            )}
            {blocks.map((b) => (
              <TableRow key={b.id} data-testid={`availability-row-${b.id.slice(0, 8)}`}>
                <TableCell className="text-sm text-stone-900">{trailerName(b.trailer)}</TableCell>
                <TableCell className={cellCls}>{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</TableCell>
                <TableCell className={cellCls}>{b.note || "—"}</TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => onRemove(b.id)}
                    data-testid={`availability-delete-${b.id.slice(0, 8)}`}
                    aria-label="Remove blocked dates"
                    className="inline-flex items-center gap-1.5 border border-stone-300 px-3 py-1.5 text-xs tracking-wide text-stone-600 transition-colors duration-300 hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Remove
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs font-light text-stone-400">
        Blocked dates appear on the booking page, and overlapping booking requests are automatically declined.
      </p>
    </div>
  );
}
